import * as React from "react";
import { useMemo } from "react";
import { useHistory, useParams } from "react-router";
import { FolderOpen, Info, Sparkles } from "lucide-react";

import {
  CoachSettings,
  PricePlanTier,
  VideoProjectDetail,
  VideoProjectMessage,
  isEditingInProgress,
} from "@/interfaces";
import { fromVideoProject, SharedPostFlowDefaults } from "@/components/PostDialogs/SharedPostFlowDefaults";
import { PageActionProps, PageTitle, PageTitleProps } from "@/components/PageTitle";
import { PlayerPostDialog } from "@/components/PostDialogs/PlayerPostDialog";
import { PageWrapper } from "@/components/PageWrapper";
import { SocialNetworksSection } from "@/components/SocialNetworksSection";
import { VideosSection } from "@/components/VideosSection";
import { Button, confirmDialog } from "@/components/ui";
import { VideoProjectServices } from "@/services";
import { VideoProject, VideoProjectServices as BedrockVideoProjectServices } from "@/services/bedrock";
import { toast } from "sonner";

import { DetailSection } from "./DetailSection";
import { ProjectCaption } from "./ProjectCaption";
import { VideoProjectBubbleDetails } from "../../Editor/VideoProjectPage/components/BubbleDetails";
import { StylePreferencesComponent } from "../../Editor/VideoProjectPage/components/StylePreferencesComponent";
import {
  URLS,
  blogDetailUrl,
  formatHexColor,
  getErrorMessage,
  urlWithFormat,
  useExistingBlog,
  useHasBlogAccess,
  usePlayerSettings,
  useSocialCoachSession,
  useSocialNetworks,
  validatePendingAck,
} from "@/utils";
import moment from "moment";
import { buildPreferenceOptions, StylePreferencesOptions } from "@/utils/projectHelpers";
import { VideoPageModal } from "@/containers/VideoPages/VideoPageModal";

const MEDIA_GRID_CLASS = "grid-cols-[repeat(auto-fill,minmax(10rem,13rem))]";

type Props = {
  embedded?: boolean;
};

const VideoProjectDetailsPageFC: React.FC<Props> = ({ embedded = false }) => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { isPlayer, playerId, session } = useSocialCoachSession();
  const videoProjectId = isFinite(Number(id)) ? Number(id) : undefined;

  // Properties
  const [loading, setLoading] = React.useState<boolean>(false);
  const [markingAsDone, setMarkingAsDone] = React.useState<boolean>(false);
  const [videoProject, setVideoProject] = React.useState<VideoProjectDetail | undefined>(undefined);
  const [videoProjectMessages, setVideoProjectMessages] = React.useState<VideoProjectMessage[] | undefined>(undefined);
  const [needsAck, setNeedsAck] = React.useState<boolean>(false);
  const [postSnUrls, setPostSnUrls] = React.useState<{ [key: string]: string }>({});
  const [showPostModal, setShowPostModal] = React.useState<boolean>(false);
  const [showVideoPageModal, setShowVideoPageModal] = React.useState<boolean>(false);
  const [preferencesInfo, setPreferencesInfo] = React.useState<StylePreferencesOptions[]>([]);
  const [showOriginalFiles, setShowOriginalFiles] = React.useState<boolean>(false);
  const { hasSetting } = usePlayerSettings(playerId);
  const hasBlogAccess = useHasBlogAccess(playerId);
  const { existingBlog, loadingExistingBlog } = useExistingBlog(
    { videoProjectId: videoProjectId ?? null },
    hasBlogAccess && !!videoProjectId
  );

  const { postableSocialNetworks } = useSocialNetworks();

  const isSCPlayer = isPlayer && session?.subscription?.tier == PricePlanTier.PLAYER_PLUS;

  const editedMedia = React.useMemo(() => {
    return videoProject?.editedMedia?.media?.[0] ?? undefined;
  }, [videoProject?.editedMedia?.media?.[0]]);

  const readyToPost = React.useMemo(() => {
    return isSCPlayer && editedMedia !== undefined;
  }, [isSCPlayer, editedMedia]);

  const canShareVideo = React.useMemo(() => {
    return readyToPost && hasSetting(CoachSettings.PLAYER_MVP_ACCESS);
  }, [readyToPost, hasSetting]);

  const canMarkAsPosted = !!videoProject && !videoProject.archived && VideoProject.canMarkAsPosted(videoProject.status);

  const hasSourceVideo = !!(videoProject?.originalMedia?.media?.length || videoProject?.editedMedia?.media?.length);
  const canGenerateBlog = hasBlogAccess && !loadingExistingBlog && !existingBlog && hasSourceVideo;

  const openExistingBlog = () => {
    if (!existingBlog) return;
    history.push(blogDetailUrl(!!isPlayer, existingBlog.id));
  };

  const generateBlog = () => {
    if (!videoProjectId) return;
    history.push(`${blogDetailUrl(!!isPlayer, "new")}?videoProjectId=${videoProjectId}`);
  };

  const goBack = () => {
    if (isSCPlayer) {
      history.replace(URLS.player.videoProjects);
    } else {
      history.goBack();
    }
  };

  React.useEffect(() => {
    setPreferencesInfo(buildPreferenceOptions(videoProject?.preferences));
  }, [videoProject?.preferences]);

  React.useEffect(() => {
    let snUrls = {};

    videoProject?.post?.socialNetworks?.map(sn => {
      if (sn.externalPostUrl && sn.socialNetworkId !== "FB") {
        snUrls = {
          ...snUrls,
          [sn.socialNetworkId]: urlWithFormat(sn.externalPostUrl!.replace("[", "").replace("]", "")),
        };
      } else {
        snUrls = {
          ...snUrls,
          [sn.socialNetworkId]: sn.externalPostUrl!,
        };
      }
    });
    setPostSnUrls(snUrls);
  }, [videoProject?.post?.socialNetworks]);

  React.useEffect(() => {
    const ROLE = isSCPlayer ? "PLAYER" : "COACH";
    if (videoProjectId) {
      setLoading(true);
      VideoProjectServices.get(videoProjectId)
        .then(vp => {
          setVideoProject(vp);
          setVideoProjectMessages(vp.messages);
          setNeedsAck(validatePendingAck(vp.ackList, ROLE));
        })
        .catch(() => {
          alert("Error getting video project info");
          goBack();
        })
        .finally(() => {
          setLoading(false);
        });
      BedrockVideoProjectServices.acknowledge(videoProjectId).catch(e => {
        console.error("acknowledge error", e);
      });
    }
  }, [videoProjectId]);

  const markAsDone = async () => {
    if (!videoProjectId) return;

    const confirmed = await confirmDialog({
      title: "Mark this project as done?",
      description:
        "It will move to Posted and any scheduled post for it will be canceled. It won't count as a post on your social networks, and this can't be undone.",
      confirmLabel: "Mark as done",
      cancelLabel: "Cancel",
      size: "md",
    });
    if (!confirmed) return;

    setMarkingAsDone(true);
    try {
      await BedrockVideoProjectServices.update(videoProjectId, { event: VideoProject.Event.MARK_AS_POSTED });
      toast.success("Project marked as done");
      VideoProjectServices.get(videoProjectId).then(setVideoProject);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setMarkingAsDone(false);
    }
  };

  const refreshMessages = (): void => {
    VideoProjectServices.get(`${videoProject?.videoProjectId!}`).then((r: VideoProjectDetail) => {
      setVideoProjectMessages(r.messages);
    });
  };

  const projectInfoSection = (project: VideoProjectDetail) => {
    const postSnIds = project.post?.socialNetworks?.filter(sn => sn.status === "POSTED").map(sn => sn.socialNetworkId);
    return (
      <DetailSection icon={Info} iconClassName="text-sky-600" title="Project Info">
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
          <dt className="font-semibold text-slate-700">Status</dt>
          <dd className="m-0 text-slate-600">
            {project.statusLabel}
            {project.statusDate && ` · ${moment(project.statusDate).format("MM/DD/YYYY h:mm a")}`}
          </dd>
          <dt className="font-semibold text-slate-700">Created</dt>
          <dd className="m-0 text-slate-600">{moment(project.createdDate).format("MM/DD/YYYY h:mm a")}</dd>
        </dl>
        {postSnIds && postSnIds.length > 0 && (
          <SocialNetworksSection actives={postSnIds} links={postSnUrls} allSocialNetworks={postableSocialNetworks} />
        )}
      </DetailSection>
    );
  };

  const projectFilesSections = (project: VideoProjectDetail) => {
    const editedVideos = project.editedMedia?.media ?? [];
    const originalVideos = project.originalMedia?.media ?? [];
    const hasEditedVideos = editedVideos.length > 0;
    const canToggleOriginals = hasEditedVideos && originalVideos.length > 0;

    return (
      <>
        {hasEditedVideos && (
          <DetailSection icon={Sparkles} iconClassName="text-indigo-500" title="Final Edit">
            <VideosSection
              type="EDITED"
              videos={editedVideos}
              videoProjectId={project.videoProjectId!}
              variant="original"
              gridClassName={MEDIA_GRID_CLASS}
            />
          </DetailSection>
        )}
        <DetailSection
          icon={FolderOpen}
          iconClassName="text-slate-500"
          title="Original Files"
          action={
            canToggleOriginals && (
              <Button
                variant="ghost"
                size="xs"
                className="text-indigo-600 hover:text-indigo-700"
                onClick={() => setShowOriginalFiles(!showOriginalFiles)}
              >
                {`${showOriginalFiles ? "Hide" : "Show"} ${originalVideos.length} ${
                  originalVideos.length === 1 ? "file" : "files"
                }`}
              </Button>
            )
          }
        >
          {(!canToggleOriginals || showOriginalFiles) && (
            <VideosSection
              type="ORIGINAL"
              videos={originalVideos}
              videoProjectId={project.videoProjectId!}
              gridClassName={MEDIA_GRID_CLASS}
            />
          )}
        </DetailSection>
      </>
    );
  };

  const postDefaults: SharedPostFlowDefaults | undefined = React.useMemo(() => {
    return videoProject ? fromVideoProject(videoProject) : undefined;
  }, [videoProject]);

  const videoPageModalInitialObject = useMemo(() => {
    if (editedMedia) return { media: { ...editedMedia, id: -1 } };
    return undefined;
  }, [editedMedia]);

  const content = () => {
    return (
      <>
        {videoProject && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
            <div className="flex min-w-0 flex-col gap-6 lg:col-span-3">
              {projectFilesSections(videoProject)}
              <ProjectCaption videoProject={videoProject} />
            </div>
            <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
              {projectInfoSection(videoProject)}
              <StylePreferencesComponent preferencesInfo={preferencesInfo} formatHexColor={formatHexColor} />
              {videoProjectMessages && (
                <VideoProjectBubbleDetails
                  onSuccess={returnedProject => {
                    if (returnedProject && returnedProject.id !== videoProjectId) {
                      const url = (isPlayer ? URLS.player.videoProject : URLS.coach.videoProject).replace(
                        ":id",
                        `${returnedProject.id}`
                      );
                      history.replace(url);
                    } else {
                      refreshMessages();
                    }
                  }}
                  videoProjectId={videoProject.videoProjectId!}
                  status={videoProject.status}
                  placeholder="Add a message to request a change..."
                  submitBtnLabel={isEditingInProgress(videoProject.status) ? "Add Message" : "Request Changes"}
                  noteMessage={
                    !videoProject.editable
                      ? "Note: Because this project has already been posted, this request will create a new project"
                      : undefined
                  }
                  messages={videoProjectMessages}
                  needsAck={needsAck}
                />
              )}
            </div>
          </div>
        )}

        {canShareVideo && (
          <VideoPageModal
            open={showVideoPageModal}
            initialObject={videoPageModalInitialObject}
            onClose={() => setShowVideoPageModal(false)}
          />
        )}

        {readyToPost && showPostModal && postDefaults && (
          <PlayerPostDialog
            open={showPostModal}
            postId={videoProject?.post?.postId}
            playerId={videoProject?.playerId}
            defaults={postDefaults}
            handleClose={() => {
              setShowPostModal(false);
            }}
          />
        )}
      </>
    );
  };

  const blogActions: PageActionProps[] = [];
  if (existingBlog && hasBlogAccess) {
    blogActions.push({ text: "View Blog", onClick: openExistingBlog });
  }
  if (canGenerateBlog) {
    blogActions.push({ text: "Generate Blog", onClick: generateBlog });
  }
  const [firstBlogAction, ...otherBlogActions] = blogActions;

  const pageTitleProps: PageTitleProps = {
    title: "Video Catalyst Project",
    breadcrumbs: [{ name: "Library", link: isPlayer ? URLS.player.videoProjects : URLS.coach.videoProjects }],
    primaryAction: readyToPost ? { text: "Post", onClick: () => setShowPostModal(true) } : null,
    secondaryAction: canShareVideo
      ? { text: "Create Video Page", onClick: () => setShowVideoPageModal(true) }
      : firstBlogAction,
    actions: [
      ...(canShareVideo ? blogActions : otherBlogActions),
      ...(canMarkAsPosted ? [{ text: "Mark as done", onClick: markAsDone, disabled: markingAsDone }] : []),
    ],
  };

  if (embedded) {
    return (
      <div>
        <PageTitle {...pageTitleProps} />
        {content()}
      </div>
    );
  }

  return (
    <PageWrapper loading={loading} {...pageTitleProps}>
      {content()}
    </PageWrapper>
  );
};

export const VideoProjectDetailsPage = VideoProjectDetailsPageFC;
